import { useState } from 'react';
import { subscriptionViewModel } from '../view-model/professional/subscription-view-model';
import uiStore from '../store/ui-store';

type LimitType = 'companies' | 'collaborators' | 'services' | 'promoCodes';

interface LimitCheckResult {
  canCreate: boolean;
  currentCount: number;
  limit: number;
  showModal: boolean;
  limitType: LimitType | null;
}

/**
 * Hook personnalisé pour gérer les limites d'abonnement
 * Affiche automatiquement un modal user-friendly quand une limite est atteinte
 * Ferme automatiquement tous les modals ouverts quand une limite est détectée
 */
export const useSubscriptionLimit = () => {
  const [limitState, setLimitState] = useState<LimitCheckResult>({
    canCreate: true,
    currentCount: 0,
    limit: 0,
    showModal: false,
    limitType: null
  });

  /**
   * Affiche le modal de limite et ferme tous les autres modals
   */
  const showLimitModal = (limitType: LimitType, currentCount: number, limit: number) => {
    // Ferme tous les modals ouverts pour que l'utilisateur voie le message
    uiStore.closeAllModals();

    // Affiche le modal de limite
    setLimitState({
      canCreate: false,
      currentCount,
      limit,
      showModal: true,
      limitType
    });
  };

  /**
   * Vérifie si l'utilisateur peut créer une company
   * Affiche un modal si la limite est atteinte et ferme les autres modals
   */
  const checkCompanyLimit = async (): Promise<boolean> => {
    const result = await subscriptionViewModel.canCreateCompany();

    if (!result.canCreate) {
      showLimitModal('companies', result.currentCount, result.limit);
      return false;
    }

    return true;
  };

  /**
   * Vérifie si l'utilisateur peut créer un collaborateur
   * Affiche un modal si la limite est atteinte et ferme les autres modals
   */
  const checkCollaboratorLimit = async (): Promise<boolean> => {
    const result = await subscriptionViewModel.canCreateCollaborator();

    if (!result.canCreate) {
      showLimitModal('collaborators', result.currentCount, result.limit);
      return false;
    }

    return true;
  };

  /**
   * Déclenche une erreur de limite manuellement
   * Utilisé quand une API retourne une erreur de limite
   */
  const triggerLimitError = (limitType: LimitType, currentCount: number, limit: number) => {
    showLimitModal(limitType, currentCount, limit);
  };

  /**
   * Ferme le modal de limite
   */
  const closeModal = () => {
    setLimitState(prev => ({
      ...prev,
      showModal: false
    }));
  };

  return {
    limitState,
    checkCompanyLimit,
    checkCollaboratorLimit,
    triggerLimitError,
    closeModal
  };
};
